import React, { useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Stack,
    Avatar,
    Chip,
    Drawer,
    IconButton,
    Pagination,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Tooltip as MuiTooltip,
    Paper,
    useTheme,
} from '@mui/material';
import { Close as CloseIcon, Launch as LaunchIcon, Topic as TopicIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTopicLabel } from './useTopicLabel';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
    Cell,
    ReferenceLine,
} from 'recharts';
import { fetchDailyPoliticalMentions } from '../../api/podcasts';
import apiService from '../../services/api';
import { convertToCSV, downloadCSV, createDateRangeFilename } from '../../utils/csvUtils';
import { CsvDownloadButton } from '../CsvDownloadButton';
import {
    TOPIC_COLORS,
    ELECTION_MILESTONES,
} from './electionColors';

// Custom label component for reference lines with tooltip
const ReferenceLineLabel = ({ viewBox, value, fill, fullLabel }: any) => {
    const theme = useTheme();
    const { x, y } = viewBox;
    // Position label ABOVE the chart area, centered on the reference line
    const labelY = y - 18;
    const rectHeight = 20;
    const rectWidth = 70;

    // Always center the label horizontally on the reference line
    const rectX = x - rectWidth / 2;
    const textX = x;

    return (
        <g>
            <title>{fullLabel}</title>
            <rect
                x={rectX}
                y={labelY - rectHeight / 2}
                width={rectWidth}
                height={rectHeight}
                rx={6}
                fill={theme.palette.background.paper}
                stroke={fill}
                strokeWidth={1.5}
                style={{ cursor: 'help', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
            />
            <text
                x={textX}
                y={labelY}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={fill}
                fontSize={10}
                fontWeight={600}
                style={{ cursor: 'help' }}
            >
                {value}
            </text>
        </g>
    );
};

// Helper to find the formatted date label for a milestone in chartData
const findMilestoneLabel = (chartData: any[], targetDate: string): string | null => {
    // For weekly data, find the week that contains the target date
    const targetDateObj = new Date(targetDate + 'T12:00:00.000Z');

    for (const entry of chartData) {
        const entryDate = new Date(entry.fullDate + 'T12:00:00.000Z');

        // Check if dates match exactly (for daily view)
        if (entry.fullDate === targetDate) {
            return entry.date;
        }

        // For weekly view, check if target falls within the week (entry date to +6 days)
        const weekEnd = new Date(entryDate);
        weekEnd.setDate(weekEnd.getDate() + 6);

        if (targetDateObj >= entryDate && targetDateObj <= weekEnd) {
            return entry.date;
        }
    }
    return null;
};



interface TopicsTabProps {
    selectedRange: {
        from: string;
        to: string;
        defaultView: 'daily' | 'weekly';
    };
    includeRadio: boolean;
    isActive: boolean;
}

interface DailyMentionData {
    date: string;
    [topic: string]: any;
}

interface TopicSelectorProps {
    topicStats: Array<{
        name: string;
        mentions: number;
        color: string;
    }>;
    selectedTopics: string[];
    onSelectionChange: (topics: string[]) => void;
    label: string;
}

// Reusable Topic Selector Component
const TopicSelector = ({ topicStats, selectedTopics, onSelectionChange, label }: TopicSelectorProps) => {
    const { t } = useTranslation('elecciones');
    const topicLabel = useTopicLabel();
    const handleChange = (event: any) => {
        const value = event.target.value;
        onSelectionChange(typeof value === 'string' ? value.split(',') : value);
    };

    return (
        <FormControl sx={{ minWidth: 300 }}>
            <InputLabel id={`topic-select-label-${label.replace(/\s+/g, '-').toLowerCase()}`}>{label}</InputLabel>
            <Select
                labelId={`topic-select-label-${label.replace(/\s+/g, '-').toLowerCase()}`}
                multiple
                value={selectedTopics}
                onChange={handleChange}
                input={<OutlinedInput label={label} />}
                renderValue={(selected) => t('fields.topicsSelected', { count: selected.length })}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 400,
                        },
                    },
                }}
            >
                {topicStats.map((topic) => (
                    <MenuItem
                        key={topic.name}
                        value={topic.name}
                        sx={{
                            pl: 1,
                            borderRadius: 1,
                        }}
                    >
                        <Checkbox
                            checked={selectedTopics.indexOf(topic.name) > -1}
                            sx={{
                                color: topic.color,
                                '& .MuiSvgIcon-root': {
                                    borderRadius: '50%',
                                },
                                '&.Mui-checked .MuiSvgIcon-root': {
                                    backgroundColor: topic.color,
                                    borderRadius: '50%',
                                },
                                mr: 1,
                                p: 0.5,
                            }}
                            icon={<Box sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                border: `2px solid ${topic.color}`,
                                backgroundColor: 'transparent'
                            }} />}
                            checkedIcon={<Box sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                backgroundColor: topic.color,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '12px',
                                fontWeight: 'bold'
                            }}>✓</Box>}
                        />
                        <Avatar
                            sx={{ width: 24, height: 24, mr: 1, bgcolor: topic.color }}
                        >
                            <TopicIcon sx={{ fontSize: 14 }} />
                        </Avatar>
                        <ListItemText
                            primary={topicLabel(topic.name)}
                            secondary={`${t('mentions.count', { count: topic.mentions })}`}
                        />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export function TopicsTab({ selectedRange, includeRadio, isActive }: TopicsTabProps) {
    const theme = useTheme();
    const { t } = useTranslation('elecciones');
    const topicLabel = useTopicLabel();
    const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedTopicsForChart, setSelectedTopicsForChart] = useState<string[]>([]);
    const [selectedTopicsForBarChart, setSelectedTopicsForBarChart] = useState<string[]>([]);
    const [weeklyStatus, setWeeklyStatus] = useState<'daily' | 'weekly'>(selectedRange.defaultView);

    // Update weeklyStatus when selectedRange changes
    React.useEffect(() => {
        setWeeklyStatus(selectedRange.defaultView);
    }, [selectedRange.defaultView]);

    // Fetch daily mention data for topics - only when tab is active
    const { data: dailyMentions = [], isLoading, error } = useQuery<DailyMentionData[]>({
        queryKey: ['daily-political-mentions-topics', selectedRange.from, selectedRange.to, includeRadio, weeklyStatus],
        queryFn: async () => {
            return fetchDailyPoliticalMentions({
                from: selectedRange.from,
                to: selectedRange.to,
                includeRadio,
                entityType: 'topic',
                period: weeklyStatus,
            });
        },
        enabled: isActive,
        staleTime: 24 * 60 * 60 * 1000, // 1 day
        gcTime: 24 * 60 * 60 * 1000, // 1 day
    });

    // Fetch topic mentions for sidebar
    const { data: topicMentionsData, isLoading: isLoadingMentions } = useQuery({
        queryKey: ['topic-mentions', selectedTopic, selectedRange.from, selectedRange.to, includeRadio, currentPage],
        queryFn: async () => {
            if (!selectedTopic) return null;
            return apiService.fetchTopicMentions({
                topicName: selectedTopic,
                from: selectedRange.from,
                to: selectedRange.to,
                includeRadio,
                page: currentPage,
                limit: 10,
            });
        },
        enabled: !!selectedTopic && sidebarOpen,
        staleTime: 24 * 60 * 60 * 1000, // 1 day
        gcTime: 24 * 60 * 60 * 1000, // 1 day
    });

    const handleTopicClick = (topicName: string) => {
        setSelectedTopic(topicName);
        setCurrentPage(1);
        setSidebarOpen(true);
    };

    const handleCloseSidebar = () => {
        setSidebarOpen(false);
        setSelectedTopic(null);
        setCurrentPage(1);
    };



    const createYouTubeUrlWithTimestamp = (url: string, startTime: number) => {
        try {
            const urlObj = new URL(url);
            const videoId = urlObj.searchParams.get('v') || urlObj.pathname.split('/').pop();
            const timestampSeconds = Math.floor(startTime);
            return `https://www.youtube.com/watch?v=${videoId}&t=${timestampSeconds}s`;
        } catch {
            return url;
        }
    };

    // CSV Download Functions
    const downloadDailyMentionsCSV = async () => {
        if (!chartData.length) return;

        try {
            // Fetch data for both filter states
            const [podcastsOnlyData, allChannelsData] = await Promise.all([
                fetchDailyPoliticalMentions({
                    from: selectedRange.from,
                    to: selectedRange.to,
                    includeRadio: false,
                    entityType: 'topic',
                    period: weeklyStatus,
                }),
                fetchDailyPoliticalMentions({
                    from: selectedRange.from,
                    to: selectedRange.to,
                    includeRadio: true,
                    entityType: 'topic',
                    period: weeklyStatus,
                })
            ]);

            // Create a map for easier data lookup
            const podcastsMap = new Map();
            const allChannelsMap = new Map();

            podcastsOnlyData.forEach((dayData: any) => {
                Object.keys(dayData).forEach(key => {
                    if (key !== 'date' && key !== 'fullDate') {
                        const mapKey = `${dayData.date}_${key}`;
                        // Handle the nested structure with .count - backend returns objects with count property
                        const value = typeof dayData[key] === 'object' && dayData[key]?.count !== undefined
                            ? dayData[key].count
                            : (typeof dayData[key] === 'number' ? dayData[key] : 0);
                        podcastsMap.set(mapKey, value);
                    }
                });
            });

            allChannelsData.forEach((dayData: any) => {
                Object.keys(dayData).forEach(key => {
                    if (key !== 'date' && key !== 'fullDate') {
                        const mapKey = `${dayData.date}_${key}`;
                        // Handle the nested structure with .count - backend returns objects with count property
                        const value = typeof dayData[key] === 'object' && dayData[key]?.count !== undefined
                            ? dayData[key].count
                            : (typeof dayData[key] === 'number' ? dayData[key] : 0);
                        allChannelsMap.set(mapKey, value);
                    }
                });
            });

            // Get all unique dates
            const allDates = Array.from(new Set([...podcastsOnlyData.map((item: any) => item.date), ...allChannelsData.map((item: any) => item.date)])).sort();

            const csvData = allDates.map(date => {
                const row: any = {
                    'Fecha': date,
                };

                // Add each selected topic's mentions for this day
                selectedTopicsForChart.forEach(topicName => {
                    const podcastsKey = `${date}_${topicName}`;
                    const podcastsOnly = podcastsMap.get(podcastsKey) || 0;
                    const allChannels = allChannelsMap.get(podcastsKey) || 0;

                    const displayName = topicLabel(topicName);
                    row[`${displayName} - Total`] = allChannels;
                    row[`${displayName} - Solo Podcasts`] = podcastsOnly;
                });

                return row;
            });

            // Create grouped header row for side-by-side presentation
            const headerRow: any = { 'Fecha': '' };
            selectedTopicsForChart.forEach(topicName => {
                const displayName = topicLabel(topicName);
                headerRow[`${displayName} - Total`] = displayName;
                headerRow[`${displayName} - Solo Podcasts`] = '';
            });

            // Create subheader row
            const subHeaderRow: any = { 'Fecha': 'Fecha' };
            selectedTopicsForChart.forEach(topicName => {
                const displayName = topicLabel(topicName);
                subHeaderRow[`${displayName} - Total`] = 'Total';
                subHeaderRow[`${displayName} - Solo Podcasts`] = 'Solo Podcasts';
            });

            // Combine header rows with data
            const csvDataWithHeaders = [headerRow, subHeaderRow, ...csvData];
            const csvContent = convertToCSV(csvDataWithHeaders);
            const filename = createDateRangeFilename(`menciones_${weeklyStatus === 'daily' ? 'diarias' : 'semanales'}_temas_completo`, selectedRange.from, selectedRange.to);
            downloadCSV(csvContent, filename);
        } catch (error) {
            console.error('Error downloading daily mentions CSV:', error);
        }
    };

    const downloadTotalMentionsCSV = async () => {
        if (!topicStats.length) return;

        try {
            // Fetch stats for podcasts only
            const podcastsOnlyData = await fetchDailyPoliticalMentions({
                from: selectedRange.from,
                to: selectedRange.to,
                includeRadio: false,
                entityType: 'topic',
                period: weeklyStatus,
            });

            // Calculate totals for podcasts only
            const podcastsTotals = new Map();
            podcastsOnlyData.forEach((dayData: any) => {
                Object.keys(dayData).forEach(topicName => {
                    if (topicName !== 'date' && topicName !== 'fullDate') {
                        const current = podcastsTotals.get(topicName) || 0;
                        // Handle the nested structure with .count - backend returns objects with count property
                        const value = typeof dayData[topicName] === 'object' && dayData[topicName]?.count !== undefined
                            ? dayData[topicName].count
                            : (typeof dayData[topicName] === 'number' ? dayData[topicName] : 0);
                        podcastsTotals.set(topicName, current + value);
                    }
                });
            });

            // Calculate grand totals for percentages
            const grandTotalPodcasts = Array.from(podcastsTotals.values()).reduce((sum, val) => sum + val, 0);

            const filteredTopics = topicStats.filter(topic => selectedTopicsForBarChart.includes(topic.name));

            const csvData = filteredTopics.map((topic) => {
                const podcastsTotal = podcastsTotals.get(topic.name) || 0;
                const allChannelsTotal = topic.mentions;

                return {
                    'Tema': topicLabel(topic.name),
                    'Total - Total': allChannelsTotal,
                    'Total - Porcentaje': totalMentions > 0 ? ((allChannelsTotal / totalMentions) * 100).toFixed(2).replace('.', ',') : '0,00',
                    'Solo Podcasts - Total': podcastsTotal,
                    'Solo Podcasts - Porcentaje': grandTotalPodcasts > 0 ? ((podcastsTotal / grandTotalPodcasts) * 100).toFixed(2).replace('.', ',') : '0,00',
                };
            });

            // Create grouped header row for side-by-side presentation
            const headerRow: any = { 'Tema': '' };
            headerRow['Total - Total'] = 'Total';
            headerRow['Total - Porcentaje'] = '';
            headerRow['Solo Podcasts - Total'] = 'Solo Podcasts';
            headerRow['Solo Podcasts - Porcentaje'] = '';

            // Create subheader row
            const subHeaderRow: any = { 'Tema': 'Tema' };
            subHeaderRow['Total - Total'] = 'Total';
            subHeaderRow['Total - Porcentaje'] = 'Porcentaje';
            subHeaderRow['Solo Podcasts - Total'] = 'Total';
            subHeaderRow['Solo Podcasts - Porcentaje'] = 'Porcentaje';

            // Combine header rows with data
            const csvDataWithHeaders = [headerRow, subHeaderRow, ...csvData];
            const csvContent = convertToCSV(csvDataWithHeaders);
            const filename = createDateRangeFilename('total_menciones_temas_completo', selectedRange.from, selectedRange.to);
            downloadCSV(csvContent, filename);
        } catch (error) {
            console.error('Error downloading total mentions CSV:', error);
        }
    };

    const downloadRankingCSV = async () => {
        if (!topicStats.length) return;

        try {
            // Fetch detailed stats for podcasts only
            const podcastsOnlyData = await fetchDailyPoliticalMentions({
                from: selectedRange.from,
                to: selectedRange.to,
                includeRadio: false,
                entityType: 'topic',
                period: weeklyStatus,
            });

            // Calculate totals and stats for podcasts only
            const podcastsStats = new Map();
            podcastsOnlyData.forEach((dayData: any) => {
                Object.keys(dayData).forEach(topicName => {
                    if (topicName !== 'date' && topicName !== 'fullDate') {
                        const current = podcastsStats.get(topicName) || { mentions: 0, days: 0 };
                        // Handle the nested structure with .count - backend returns objects with count property
                        const value = typeof dayData[topicName] === 'object' && dayData[topicName]?.count !== undefined
                            ? dayData[topicName].count
                            : (typeof dayData[topicName] === 'number' ? dayData[topicName] : 0);
                        current.mentions += value;
                        if (value > 0) {
                            current.days += 1;
                        }
                        podcastsStats.set(topicName, current);
                    }
                });
            });

            const csvData = topicStats.map((topic, index) => {
                const podcastsStat = podcastsStats.get(topic.name) || { mentions: 0, days: 0 };

                return {
                    'Posición': index + 1,
                    'Tema': topicLabel(topic.name),
                    'Podcasts + Radio/TV - Total Menciones': topic.mentions,
                    [`Podcasts + Radio/TV - ${t('charts.activeDays')}`]: topic.daysActive,
                    'Podcasts + Radio/TV - Promedio por Día': topic.avgMentionsPerDay.toString().replace('.', ','),
                    'Podcasts + Radio/TV - Promedio por Episodio': topic.avgMentionsPerEpisode.toString().replace('.', ','),
                    'Podcasts + Radio/TV - Duración (horas)': topic.duration.toString().replace('.', ','),
                    'Podcasts - Total Menciones': podcastsStat.mentions,
                    [`Podcasts - ${t('charts.activeDays')}`]: podcastsStat.days,
                    'Podcasts - Promedio por Día': podcastsStat.days > 0 ? (podcastsStat.mentions / podcastsStat.days).toFixed(2).replace('.', ',') : '0,00',
                    'Período': `${selectedRange.from} a ${selectedRange.to}`,
                };
            });

            const csvContent = convertToCSV(csvData);
            const filename = createDateRangeFilename('ranking_temas_completo', selectedRange.from, selectedRange.to);
            downloadCSV(csvContent, filename);
        } catch (error) {
            console.error('Error downloading ranking CSV:', error);
        }
    };

    // Process data for charts
    const { chartData, topicStats, totalMentions } = useMemo(() => {
        if (!dailyMentions.length) {
            return { chartData: [], topicStats: [], totalMentions: 0 };
        }

        // Get all unique topics
        const topicsSet = new Set<string>();
        dailyMentions.forEach(day => {
            Object.keys(day).forEach(key => {
                if (key !== 'date' && key !== 'fullDate') {
                    // Handle both object structure (new) and direct number (legacy)
                    const hasData = typeof day[key] === 'object'
                        ? (day[key]?.count > 0)
                        : (typeof day[key] === 'number' && day[key] > 0);

                    if (hasData) {
                        const cleanKey = key?.toString().trim();
                        if (cleanKey && cleanKey !== 'undefined' && cleanKey !== 'null') {
                            topicsSet.add(cleanKey);
                        }
                    }
                }
            });
        });

        const topics = Array.from(topicsSet);

        // Transform data for line chart
        const chartData = dailyMentions.map(day => {
            // Backend now handles daily/weekly aggregation
            // For weekly data, the date will be the week start date
            const dateObj = new Date(day.date + 'T12:00:00.000Z'); // Add time to avoid timezone issues

            let formattedDate;
            if (weeklyStatus === 'weekly') {
                // Format as "Sem 30 Jun" for weekly data
                formattedDate = `${t('charts.weekShort')} ${dateObj.toLocaleDateString('en-GB', {
                    timeZone: 'America/Santiago',
                    day: 'numeric',
                    month: 'short'
                })}`;
            } else {
                // Format as "30 Jun" for daily data
                formattedDate = dateObj.toLocaleDateString('en-GB', {
                    timeZone: 'America/Santiago',
                    day: 'numeric',
                    month: 'short'
                });
            }

            const transformedDay: any = {
                date: formattedDate,
                fullDate: day.date,
            };

            topics.forEach(topic => {
                // Handle both object structure (new) and direct number (legacy)
                transformedDay[topic] = typeof day[topic] === 'object' && day[topic]?.count !== undefined
                    ? day[topic].count
                    : (typeof day[topic] === 'number' ? day[topic] : 0);
            });

            return transformedDay;
        });

        // Calculate topic statistics
        const topicStats = topics.map(topic => {
            const totalMentions = dailyMentions.reduce((sum, day) => {
                const count = typeof day[topic] === 'object' && day[topic]?.count !== undefined
                    ? day[topic].count
                    : (typeof day[topic] === 'number' ? day[topic] : 0);
                return sum + count;
            }, 0);

            const totalDuration = dailyMentions.reduce((sum, day) => {
                const duration = typeof day[topic] === 'object' && day[topic]?.duration !== undefined
                    ? day[topic].duration
                    : 0;
                return sum + duration;
            }, 0);

            const totalEpisodes = dailyMentions.reduce((sum, day) => {
                const episodeCount = typeof day[topic] === 'object' && day[topic]?.episodeCount !== undefined
                    ? day[topic].episodeCount
                    : 0;
                return sum + episodeCount;
            }, 0);

            const daysWithMentions = dailyMentions.filter(day => {
                const count = typeof day[topic] === 'object' && day[topic]?.count !== undefined
                    ? day[topic].count
                    : (typeof day[topic] === 'number' ? day[topic] : 0);
                return count > 0;
            }).length;

            return {
                name: topic,
                mentions: totalMentions,
                duration: Math.round((totalDuration / 3600) * 10) / 10,
                daysActive: daysWithMentions,
                avgMentionsPerDay: daysWithMentions > 0 ? Math.round((totalMentions / daysWithMentions) * 10) / 10 : 0,
                avgMentionsPerEpisode: totalEpisodes > 0 ? Math.round((totalMentions / totalEpisodes) * 10) / 10 : 0,
                color: TOPIC_COLORS[topic] || '#8884d8',
            };
        }).sort((a, b) => b.mentions - a.mentions);

        const totalMentions = topicStats.reduce((sum, topic) => sum + topic.mentions, 0);

        return { chartData, topicStats, totalMentions };
    }, [dailyMentions, weeklyStatus]);

    // Initialize selected topics with top 4 when data changes
    React.useEffect(() => {
        if (topicStats.length > 0 && selectedTopicsForChart.length === 0) {
            const top4Topics = topicStats.slice(0, 4).map(t => t.name);
            setSelectedTopicsForChart(top4Topics);
        }
        if (topicStats.length > 0 && selectedTopicsForBarChart.length === 0) {
            const allTopics = topicStats.map(t => t.name);
            setSelectedTopicsForBarChart(allTopics);
        }
    }, [topicStats, selectedTopicsForChart.length, selectedTopicsForBarChart.length]);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        console.error('Error fetching daily topic mentions:', error);
        return (
            <Alert severity="error" sx={{ mb: 3 }}>
                {t('empty.loadDailyTopics', { message: error instanceof Error ? error.message : 'Unknown error' })}
            </Alert>
        );
    }

    if (!dailyMentions.length || !topicStats.length) {
        return (
            <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
                        {t('empty.title')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        {t('empty.noTopics')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('empty.because')}
                    </Typography>
                    <Box component="ul" sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto', mt: 2 }}>
                        <Typography component="li" variant="body2" color="text.secondary">
                            {t('empty.noAnalyzed')}
                        </Typography>
                        <Typography component="li" variant="body2" color="text.secondary">
                            {t('empty.stillProcessing')}
                        </Typography>
                        <Typography component="li" variant="body2" color="text.secondary">
                            {t('empty.periodNoTopics')}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Stack spacing={4}>
            {/* Summary Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                {topicStats.slice(0, 4).map((topic) => (
                    <Card
                        key={topic.name}
                        sx={{
                            cursor: 'pointer',
                            border: `2px solid ${theme.palette.divider}`,
                            backgroundColor: 'transparent',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                                border: `2px solid ${theme.palette.divider}`,
                            }
                        }}
                        onClick={() => handleTopicClick(topic.name)}
                    >
                        <CardContent sx={{ pb: 2 }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar
                                    sx={{ width: 48, height: 48, bgcolor: topic.color }}
                                >
                                    <TopicIcon />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                        {topicLabel(topic.name)}
                                    </Typography>
                                    <Typography variant="h4" color="primary.main" sx={{ fontSize: '1.5rem' }}>
                                        {topic.mentions}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('mentions.word')}
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            {/* Line Chart - Daily Mentions Over Time */}
            <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h3">
                        {weeklyStatus === 'daily' ? t('charts.mentionsByTopicDaily') : t('charts.mentionsByTopicWeekly')}
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <FormControl sx={{ minWidth: 150 }}>
                            <InputLabel id="weekly-status-label-topics">{t('period.label')}</InputLabel>
                            <Select
                                labelId="weekly-status-label-topics"
                                value={weeklyStatus}
                                onChange={(e) => setWeeklyStatus(e.target.value as 'daily' | 'weekly')}
                                input={<OutlinedInput label={t('period.label')} />}
                            >
                                <MenuItem value="daily">{t('cadence.daily')}</MenuItem>
                                <MenuItem value="weekly">{t('cadence.weekly')}</MenuItem>
                            </Select>
                        </FormControl>
                        <TopicSelector
                            topicStats={topicStats}
                            selectedTopics={selectedTopicsForChart}
                            onSelectionChange={setSelectedTopicsForChart}
                            label={t('fields.selectTopics')}
                        />
                        <CsvDownloadButton
                            onClick={downloadDailyMentionsCSV}
                            disabled={!chartData.length || selectedTopicsForChart.length === 0}
                        />
                    </Stack>
                </Box>
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid', borderColor: 'divider',
                        bgcolor: 'background.paper',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{
                        px: 3,
                        py: 2.5,
                        borderBottom: 1, borderColor: 'divider',
                        bgcolor: 'action.hover',
                    }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                                fontSize: '1.1rem',
                                letterSpacing: '-0.01em',
                            }}
                        >
                            {t('charts.evolutionByTopic')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                            {weeklyStatus === 'weekly' ? t('charts.trendTopicWeekly') : t('charts.trendTopicDaily')}
                        </Typography>
                    </Box>
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ width: '100%', height: 400 }}>
                            <ResponsiveContainer>
                                <LineChart data={chartData} margin={{ top: 30, right: 50, left: 10, bottom: 10 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                        tickLine={{ stroke: theme.palette.divider }}
                                        axisLine={{ stroke: theme.palette.divider }}
                                        interval="preserveStartEnd"
                                        type="category"
                                        scale="point"
                                        tickFormatter={(value) => value}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                        tickLine={{ stroke: theme.palette.divider }}
                                        axisLine={{ stroke: theme.palette.divider }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary,
                                            border: 'none',
                                            borderRadius: 12,
                                            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                            padding: '12px 16px',
                                        }}
                                        labelFormatter={(label, payload) => {
                                            if (payload && payload[0]) {
                                                // Fix timezone issue by adding time component
                                                const dateStr = payload[0].payload.fullDate;
                                                const dateObj = new Date(dateStr + 'T12:00:00.000Z');
                                                return dateObj.toLocaleDateString('es-CL', {
                                                    timeZone: 'America/Santiago',
                                                    weekday: 'long',
                                                    day: 'numeric',
                                                    month: 'long',
                                                    year: 'numeric'
                                                });
                                            }
                                            return label;
                                        }}
                                        formatter={(value, name) => [
                                            t('charts.tooltipMentions', { count: Number(value ?? 0) }),
                                            name as string
                                        ]}
                                        itemSorter={(item) => {
                                            return -(item.value as number);
                                        }}
                                    />
                                    <Legend />
                                    {/* Election milestone reference lines */}
                                    {ELECTION_MILESTONES.map((milestone) => {
                                        const xValue = findMilestoneLabel(chartData, milestone.date);
                                        if (!xValue) return null;
                                        return (
                                            <ReferenceLine
                                                key={milestone.date}
                                                x={xValue}
                                                stroke={milestone.color}
                                                strokeWidth={2}
                                                strokeDasharray="4 4"
                                                label={<ReferenceLineLabel value={t(`milestones.${milestone.id}.short`)} fill={milestone.color} fullLabel={t(`milestones.${milestone.id}.full`)} />}
                                            />
                                        );
                                    })}
                                    {topicStats
                                        .filter(topic => selectedTopicsForChart.includes(topic.name))
                                        .map((topic) => (
                                            <Line
                                                key={topic.name}
                                                type="monotone"
                                                dataKey={topic.name}
                                                name={topicLabel(topic.name)}
                                                stroke={topic.color}
                                                strokeWidth={2}
                                                dot={{ r: 4 }}
                                                activeDot={{ r: 6 }}
                                            />
                                        ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    </Box>
                </Paper>
            </Stack>

            {/* Bar Chart - Total Mentions */}
            <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h3">
                        {t('charts.totalByTopic')}
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TopicSelector
                            topicStats={topicStats}
                            selectedTopics={selectedTopicsForBarChart}
                            onSelectionChange={setSelectedTopicsForBarChart}
                            label={t('fields.filterTopics')}
                        />
                        <CsvDownloadButton
                            onClick={downloadTotalMentionsCSV}
                            disabled={!topicStats.length || selectedTopicsForBarChart.length === 0}
                        />
                    </Stack>
                </Box>
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid', borderColor: 'divider',
                        bgcolor: 'background.paper',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{
                        px: 3,
                        py: 2.5,
                        borderBottom: 1, borderColor: 'divider',
                        bgcolor: 'action.hover',
                    }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                                fontSize: '1.1rem',
                            }}
                        >
                            {t('charts.rankingTopics')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                            {t('charts.totalByTopicInPeriod')}
                        </Typography>
                    </Box>
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ width: '100%', height: 400 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={topicStats.filter(topic => selectedTopicsForBarChart.includes(topic.name))}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                                        tickLine={{ stroke: theme.palette.divider }}
                                        axisLine={{ stroke: theme.palette.divider }}
                                        tickFormatter={(value) => topicLabel(value)}
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        interval={0}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                        tickLine={{ stroke: theme.palette.divider }}
                                        axisLine={{ stroke: theme.palette.divider }}
                                        allowDecimals={false}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary,
                                            border: 'none',
                                            borderRadius: 12,
                                            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                            padding: '12px 16px',
                                        }}
                                        formatter={(value) => [t('charts.tooltipMentions', { count: Number(value ?? 0) }), t('charts.total')]}
                                        labelFormatter={(label) => `${label}`}
                                    />
                                    <Bar
                                        dataKey="mentions"
                                        radius={[6, 6, 0, 0]}
                                    >
                                        {topicStats
                                            .filter(topic => selectedTopicsForBarChart.includes(topic.name))
                                            .map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Box>
                </Paper>
            </Stack>


            {/* Detailed Statistics */}
            <Stack spacing={4}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h3">
                        {t('charts.rankingTopics')}
                    </Typography>
                    <CsvDownloadButton
                        onClick={downloadRankingCSV}
                        disabled={!topicStats.length}
                    />
                </Box>
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid', borderColor: 'divider',
                        bgcolor: 'background.paper',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ p: 3 }}>


                        {/* Header Row */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 2,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            mb: 2,
                            fontWeight: 600
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ minWidth: 24, textAlign: 'center', mr: 2, fontWeight: 600 }}>
                                    #
                                </Typography>
                                <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
                                    {t('fields.topic')}
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {t('charts.activeDays')}
                                    </Typography>
                                </Box>
                                <MuiTooltip
                                    title={t('charts.avgDayTipTopic')}
                                    arrow
                                    placement="top"
                                >
                                    <Box sx={{ textAlign: 'center', cursor: 'help', minWidth: 80 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {t('charts.avgPerDay')}
                                        </Typography>
                                    </Box>
                                </MuiTooltip>
                                <MuiTooltip
                                    title={t('charts.avgEpisodeTipTopic')}
                                    arrow
                                    placement="top"
                                >
                                    <Box sx={{ textAlign: 'center', cursor: 'help', minWidth: 100 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {t('charts.avgPerEpisode')}
                                        </Typography>
                                    </Box>
                                </MuiTooltip>
                                <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {t('charts.shareOfTotal')}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        {/* Data Rows */}
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            {topicStats.map((topic, index) => (
                                <Box
                                    key={topic.name}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 2,
                                        bgcolor: index % 2 === 0 ? 'grey.50' : 'transparent',
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: 'primary.50',
                                            transform: 'translateX(4px)',
                                            transition: 'all 0.2s ease-in-out'
                                        }
                                    }}
                                    onClick={() => handleTopicClick(topic.name)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{ minWidth: 24, textAlign: 'center', mr: 2 }}
                                        >
                                            #{index + 1}
                                        </Typography>
                                        <Avatar
                                            sx={{ width: 40, height: 40, mr: 2, bgcolor: topic.color }}
                                        >
                                            <TopicIcon />
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                {topicLabel(topic.name)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('mentions.count', { count: topic.mentions })}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                                            <Typography variant="h6" color="primary.main">
                                                {topic.daysActive}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                                            <Typography variant="h6" color="primary.main">
                                                {topic.avgMentionsPerDay}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center', minWidth: 100 }}>
                                            <Typography variant="h6" color="primary.main">
                                                {topic.avgMentionsPerEpisode}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                                            <Typography variant="h6" color="primary.main">
                                                {totalMentions > 0 ? Math.round((topic.mentions / totalMentions) * 100) : 0}%
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Paper>
            </Stack>

            {/* Sidebar for topic mentions */}
            <Drawer
                anchor="right"
                open={sidebarOpen}
                onClose={handleCloseSidebar}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 800,
                        maxWidth: '90vw',
                    },
                }}
            >
                <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar
                                sx={{ width: 40, height: 40, bgcolor: selectedTopic ? TOPIC_COLORS[selectedTopic] || '#8884d8' : '#8884d8' }}
                            >
                                <TopicIcon />
                            </Avatar>
                            <Box>
                                <Typography variant="h6">{selectedTopic ? topicLabel(selectedTopic) : ''}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('mentions.inPeriod')}
                                </Typography>
                            </Box>
                        </Stack>
                        <IconButton onClick={handleCloseSidebar}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    {/* Content */}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {isLoadingMentions ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                                <CircularProgress />
                            </Box>
                        ) : topicMentionsData?.mentions.length ? (
                            <Stack spacing={2}>
                                {topicMentionsData.mentions.map((mention) => (
                                    <Card key={mention._id} variant="outlined">
                                        <CardContent sx={{ pb: 2 }}>
                                            <Stack spacing={2}>
                                                {/* Episode info */}
                                                <Box>
                                                    <Typography variant="subtitle2" color="primary.main">
                                                        {mention.podcastName}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {mention.episodeTitle}
                                                    </Typography>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(mention.publishedAt).toLocaleDateString('es-CL', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric',
                                                            })}
                                                        </Typography>
                                                        {mention.episodeUrl && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    const youtubeUrl = createYouTubeUrlWithTimestamp(
                                                                        mention.episodeUrl,
                                                                        mention.segmentStart
                                                                    );
                                                                    window.open(youtubeUrl, '_blank');
                                                                }}
                                                                sx={{ p: 0.5 }}
                                                            >
                                                                <LaunchIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        )}
                                                    </Stack>
                                                </Box>

                                                {/* Mention content */}
                                                <Box>
                                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                                        {mention.mentionText}
                                                    </Typography>
                                                    {mention.context && mention.context !== mention.mentionText && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                            Contexto: {mention.context}
                                                        </Typography>
                                                    )}
                                                </Box>

                                                {/* Metadata */}
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    {mention.primaryTopicName ? (
                                                        <Chip
                                                            label={mention.primaryTopicName}
                                                            size="small"
                                                            color="primary"
                                                        />
                                                    ) : mention.subtopics.length > 0 && (
                                                        <Chip
                                                            label={mention.subtopics[0]}
                                                            size="small"
                                                            color="secondary"
                                                        />
                                                    )}
                                                    {mention.subtopics.length > 1 && (
                                                        <Chip
                                                            label={mention.subtopics.slice(0, 3).join(', ')}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="body1" color="text.secondary">
                                    {t('mentions.noneTopic')}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Pagination */}
                    {topicMentionsData?.pagination && topicMentionsData.pagination.totalPages > 1 && (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                            <Pagination
                                count={topicMentionsData.pagination.totalPages}
                                page={currentPage}
                                onChange={(_, page) => setCurrentPage(page)}
                                color="primary"
                            />
                        </Box>
                    )}
                </Box>
            </Drawer>
        </Stack>
    );
}
